// @ts-check
// The live channel — the client half of Academy.Server/Live/AcademyLiveHub.
//
// ONE CONNECTION PER TAB, opened lazily by the first subscriber and shared by
// every later one; events carry ids, never rows, and every handler here should
// respond by refetching through the API where authorization and shaping live.
// The connection is deliberately quiet about failures: a screen without the
// live channel is exactly the screen we shipped last week — manual refresh
// still tells the whole truth — so a proxy that cannot upgrade a WebSocket
// must never take the app down with it.

import { useEffect } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { apiOrigin } from '../config/environment';
import { CSRF_HEADER } from '../auth/bffTokenSource';

export const HUB_PATH = '/hubs/live';

/** Server-to-client event names — mirror AcademyLiveHub's constants. */
export const LiveEvents = {
    crmArrival: 'crmArrival',
};

/** @type {import('@microsoft/signalr').HubConnection | null} */
let connection = null;

function ensureConnection() {
    if (connection) return connection;

    // Absolute, because SignalR refuses to resolve a relative URL outside a
    // real browser document (jsdom included) — same-origin stays same-origin.
    const base = apiOrigin || (typeof window !== 'undefined' ? window.location.origin : '');

    connection = new HubConnectionBuilder()
        .withUrl(`${base}${HUB_PATH}`, {
            // No token: the same-origin session cookie authenticates the hub.
            // The header covers negotiate and long polling; the WebSocket
            // upgrade cannot carry it and is admitted on its Origin instead
            // (Academy.Server/Auth/BffCsrf.cs).
            headers: { [CSRF_HEADER]: '1' },
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

    connection.start().catch((error) => {
        // One console line, not an error dialog: see the module header.
        console.warn('[live] Hub connection failed; screens fall back to manual refresh.', error);
    });

    return connection;
}

/**
 * Subscribe to a live event for the lifetime of the component. The handler is
 * called with whatever payload the server sent (usually an id object); treat
 * it as a staleness signal and refetch.
 *
 * @param {string} event one of {@link LiveEvents}
 * @param {(payload: any) => void} handler
 */
export function useLiveEvent(event, handler) {
    useEffect(() => {
        let hub;
        try {
            hub = ensureConnection();
        } catch (error) {
            // No live channel here (an exotic environment, a test runner):
            // the screen still works, it just refreshes by hand.
            console.warn('[live] Live channel unavailable.', error);
            return undefined;
        }
        hub.on(event, handler);
        return () => hub.off(event, handler);
    }, [event, handler]);
}
